import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { downloadReport } from '../utils/downloadReport';

const tabs = [
  { key: 'pending', label: 'Por aprobar', params: { estado: 'pendiente_aprobacion' } },
  { key: 'collect', label: 'Por cobrar', params: { estado: 'aprobado' } },
  { key: 'overdue', label: 'Vencidos', params: { overdue: 'true' } },
  { key: 'collected', label: 'Cobrados', params: { estado: 'cobrado' } },
];

const monthOptions = [
  ['1', 'Enero'],
  ['2', 'Febrero'],
  ['3', 'Marzo'],
  ['4', 'Abril'],
  ['5', 'Mayo'],
  ['6', 'Junio'],
  ['7', 'Julio'],
  ['8', 'Agosto'],
  ['9', 'Septiembre'],
  ['10', 'Octubre'],
  ['11', 'Noviembre'],
  ['12', 'Diciembre'],
];

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');

const currentYear = new Date().getFullYear();

const emptyCompany = { nombre: '', ruc: '', email: '', telefono: '', activo: true };
const emptyResponsible = { company: '', nombre: '', departamento: '', email: '', telefono: '', activo: true };

const CreditReceivables = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [activeTab, setActiveTab] = useState('pending');
  const [companies, setCompanies] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loadingData, setLoadingData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    fecha_documento: '',
    year: String(currentYear),
    month: '',
    start_date: '',
    end_date: '',
    company_id: '',
    responsible_id: '',
    usuario_matrizador: '',
  });
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [responsibleModalOpen, setResponsibleModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [responsibleForm, setResponsibleForm] = useState(emptyResponsible);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [collectModalOpen, setCollectModalOpen] = useState(false);
  const [collectForm, setCollectForm] = useState({
    tipo_pago: 'transferencia',
    numero_transaccion: '',
    fecha_pago: new Date().toISOString().slice(0, 10),
    archivo: null,
    notes: '',
  });
  const [reportPeriod, setReportPeriod] = useState('month');

  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a Trámites por Cobrar.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (tabs.some((item) => item.key === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const fetchCatalogs = useCallback(async () => {
    const [companiesResponse, responsiblesResponse, matrizadoresResponse] = await Promise.all([
      api.get('/api/audit/credit-companies/'),
      api.get('/api/audit/credit-responsibles/'),
      api.get('/api/audit/matrizadores/list/'),
    ]);
    setCompanies(Array.isArray(companiesResponse.data?.results) ? companiesResponse.data.results : []);
    setResponsibles(Array.isArray(responsiblesResponse.data?.results) ? responsiblesResponse.data.results : []);
    setMatrizadores(Array.isArray(matrizadoresResponse.data) ? matrizadoresResponse.data : []);
  }, []);

  const buildParams = useCallback(() => {
    const tab = tabs.find((item) => item.key === activeTab) || tabs[0];
    const params = { ...tab.params };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') params[key] = value;
    });
    if (!params.month) {
      delete params.year;
      delete params.month;
    }
    if (params.fecha_documento) {
      delete params.year;
      delete params.month;
      delete params.start_date;
      delete params.end_date;
    }
    return params;
  }, [activeTab, filters]);

  const fetchReceivables = useCallback(async () => {
    setLoadingData(true);
    try {
      const [receivablesResponse, alertsResponse] = await Promise.all([
        api.get('/api/audit/credit-receivables/', { params: buildParams() }),
        api.get('/api/audit/credit-receivables/alerts/'),
      ]);
      setRows(Array.isArray(receivablesResponse.data?.results) ? receivablesResponse.data.results : []);
      setSummary(receivablesResponse.data?.summary || null);
      setAlerts(alertsResponse.data || null);
      setSelectedIds(new Set());
      setCurrentPage(1);
    } catch (err) {
      toast.error('No se pudieron cargar los trámites por cobrar.');
      setRows([]);
      setSummary(null);
    } finally {
      setLoadingData(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchCatalogs().catch(() => toast.error('No se pudieron cargar empresas/responsables.'));
    }
  }, [loading, user, fetchCatalogs]);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchReceivables();
    }
  }, [loading, user, activeTab, fetchReceivables]);

  const responsiblesForFilter = useMemo(() => {
    if (!filters.company_id) return responsibles;
    return responsibles.filter((responsible) => String(responsible.company) === String(filters.company_id));
  }, [filters.company_id, responsibles]);

  const selectedRows = useMemo(() => rows.filter((row) => selectedIds.has(String(row.id))), [rows, selectedIds]);
  const selectedTotal = useMemo(
    () => selectedRows.reduce((acc, row) => acc + (Number(row.monto) || 0), 0),
    [selectedRows]
  );

  const selectedPeriodLabel = useMemo(() => {
    const periods = new Set(selectedRows.map((row) => `${row.periodo_year}-${String(row.periodo_month).padStart(2, '0')}`));
    return periods.size === 1 ? Array.from(periods)[0] : '';
  }, [selectedRows]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(rows.length / pageSize)), [rows.length, pageSize]);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);
  const pageStart = rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, rows.length);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canSelectRow = (row) => {
    if (row.estado === 'cobrado') return false;
    if (activeTab === 'pending') return row.estado === 'pendiente_aprobacion';
    if (activeTab === 'collect' || activeTab === 'overdue') return row.estado === 'aprobado';
    return false;
  };

  const submitCompany = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/audit/credit-companies/', companyForm);
      toast.success('Empresa creada.');
      setCompanyForm(emptyCompany);
      setCompanyModalOpen(false);
      await fetchCatalogs();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo crear la empresa.');
    }
  };

  const submitResponsible = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/audit/credit-responsibles/', responsibleForm);
      toast.success('Responsable creado.');
      setResponsibleForm(emptyResponsible);
      setResponsibleModalOpen(false);
      await fetchCatalogs();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo crear el responsable.');
    }
  };

  const reviewSelected = async () => {
    if (!reviewModal || selectedRows.length === 0) return;
    try {
      await api.patch('/api/audit/credit-receivables/review/', {
        ids: selectedRows.map((row) => row.id),
        action: reviewModal,
        notes: reviewNotes,
      });
      toast.success(reviewModal === 'aprobar' ? 'Créditos aprobados.' : 'Créditos rechazados.');
      setReviewModal(null);
      setReviewNotes('');
      await fetchReceivables();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo revisar la selección.');
    }
  };

  const collectSelected = async (event) => {
    event.preventDefault();
    if (selectedRows.length === 0) {
      toast.error('Selecciona al menos un trámite aprobado.');
      return;
    }
    if (!selectedPeriodLabel) {
      toast.error('Selecciona trámites de un solo mes calculado.');
      return;
    }
    const formData = new FormData();
    selectedRows.forEach((row) => formData.append('ids', row.id));
    formData.append('tipo_pago', collectForm.tipo_pago);
    formData.append('numero_transaccion', collectForm.numero_transaccion);
    formData.append('fecha_pago', collectForm.fecha_pago);
    formData.append('monto_total', selectedTotal.toFixed(2));
    formData.append('notes', collectForm.notes || '');
    if (collectForm.archivo) formData.append('archivo', collectForm.archivo);
    try {
      await api.patch('/api/audit/credit-receivables/collect/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Pago mensual registrado.');
      setCollectModalOpen(false);
      setCollectForm({
        tipo_pago: 'transferencia',
        numero_transaccion: '',
        fecha_pago: new Date().toISOString().slice(0, 10),
        archivo: null,
        notes: '',
      });
      await fetchReceivables();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo registrar el cobro.');
    }
  };

  const handleDownloadReport = useCallback(
    async (format) => {
      const tab = tabs.find((item) => item.key === activeTab) || tabs[0];
      const params = { ...tab.params, period: reportPeriod };
      if (reportPeriod === 'day') {
        if (!filters.fecha_documento) {
          toast.error('Selecciona un día para generar el reporte.');
          return;
        }
        params.date = filters.fecha_documento;
      } else if (reportPeriod === 'month') {
        if (!filters.year || !filters.month) {
          toast.error('Selecciona año y mes para generar el reporte.');
          return;
        }
        params.year = filters.year;
        params.month = filters.month;
      } else if (reportPeriod === 'year') {
        if (!filters.year) {
          toast.error('Selecciona un año para generar el reporte.');
          return;
        }
        params.year = filters.year;
      }
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.responsible_id) params.responsible_id = filters.responsible_id;
      if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;
      await downloadReport({
        api,
        endpoint: '/api/audit/reports/credit-receivables/',
        params,
        format,
        baseName: 'reporte-creditos',
      });
    },
    [activeTab, reportPeriod, filters]
  );

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gray-900 text-white p-4 sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaría 4 Cantón Otavalo" className="w-12 h-auto sm:w-14" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Trámites por Cobrar</h1>
            <p className="text-sm text-gray-300">Créditos mensuales por matrizador, empresa y responsable</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCompanyModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Empresa
          </button>
          <button
            onClick={() => setResponsibleModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Responsable
          </button>
          <button
            onClick={fetchReceivables}
            className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loadingData ? 'animate-spin' : ''}`} />
          </button>
          <Link to="/dashboard/notario" className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
            Volver
          </Link>
        </div>
      </header>

      {alerts && (alerts.overdue_count > 0 || alerts.pending_approval_count > 0) && (
        <section className="mb-6 rounded-lg border border-notaria-yellow/40 bg-notaria-yellow/10 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-notaria-yellow" />
            <p className="font-semibold">
              {alerts.overdue_count} vencidos ({toMoney(alerts.overdue_total)}) · {alerts.pending_approval_count} por aprobar · {alerts.approved_pending_count} aprobados por cobrar
            </p>
          </div>
        </section>
      )}

      <main className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-4 py-3 text-sm font-bold transition ${
                  activeTab === tab.key ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2/70 text-white hover:bg-notaria-blue-3'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              type="date"
              value={filters.fecha_documento}
              onChange={(e) => setFilters((prev) => ({ ...prev, fecha_documento: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Filtrar por día"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={filters.year}
                onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Año"
              />
              <select
                value={filters.month}
                onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Mes"
              >
                <option value="">Mes</option>
                {monthOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <select
              value={filters.company_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, company_id: e.target.value, responsible_id: '' }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Empresa"
            >
              <option value="">Todas las empresas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.nombre}</option>
              ))}
            </select>
            <select
              value={filters.responsible_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, responsible_id: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Responsable"
            >
              <option value="">Todos los responsables</option>
              {responsiblesForFilter.map((responsible) => (
                <option key={responsible.id} value={responsible.id}>
                  {responsible.nombre}{responsible.departamento ? ` - ${responsible.departamento}` : ''}
                </option>
              ))}
            </select>
            <select
              value={filters.usuario_matrizador}
              onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Matrizador"
            >
              <option value="">Todos los matrizadores</option>
              {matrizadores.map((matrizador) => (
                <option key={matrizador.nombre} value={normalizeText(matrizador.nombre)}>
                  {matrizador.nombre}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Desde"
            />
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Hasta"
            />
            <button
              type="button"
              onClick={fetchReceivables}
              className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
            >
              Aplicar filtros
            </button>
          </div>

          <div className="mt-4 border-t border-white/10 pt-4">
            <p className="mb-3 text-sm font-semibold text-gray-200">Reporte Excel con los filtros actuales</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Periodo del reporte"
              >
                <option value="day">Por día</option>
                <option value="month">Por mes</option>
                <option value="year">Por año</option>
              </select>
              <button
                type="button"
                onClick={() => handleDownloadReport('xlsx')}
                className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
              >
                Descargar XLSX
              </button>
              <button
                type="button"
                onClick={() => handleDownloadReport('xls')}
                className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-bold text-white hover:bg-notaria-blue-3"
              >
                Descargar XLS
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="rounded-lg bg-notaria-blue-1/80 p-4">
            <p className="text-sm text-gray-300">Total visible</p>
            <p className="text-2xl font-bold">{toMoney(summary?.total)}</p>
            <p className="text-sm text-gray-300">{summary?.count || 0} trámites</p>
          </div>
          <SummaryList title="Por empresa" rows={summary?.by_company || []} />
          <SummaryList title="Por responsable" rows={summary?.by_responsible || []} />
          <SummaryList title="Por matrizador" rows={summary?.by_matrizador || []} />
        </section>

        {selectedRows.length > 0 && (
          <section className="rounded-lg border border-white/10 bg-notaria-blue-1/80 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{selectedRows.length} seleccionados · {toMoney(selectedTotal)}</p>
                <p className="text-sm text-gray-300">{selectedPeriodLabel ? `Mes calculado: ${selectedPeriodLabel}` : 'Selección de varios meses'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeTab === 'pending' && (
                  <>
                    <button onClick={() => setReviewModal('aprobar')} className="rounded-lg bg-green-600 px-4 py-2 font-bold hover:bg-green-500">Aprobar</button>
                    <button onClick={() => setReviewModal('rechazar')} className="rounded-lg bg-notaria-red px-4 py-2 font-bold hover:bg-red-500">Rechazar</button>
                  </>
                )}
                {(activeTab === 'collect' || activeTab === 'overdue') && (
                  <button onClick={() => setCollectModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
                    <BanknotesIcon className="h-5 w-5" />
                    Justificar cobro mensual
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-200">
            <div>
              Mostrando {pageStart}-{pageEnd} de {rows.length} trámites
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="credit-page-size" className="text-gray-300">Filas</label>
              <select
                id="credit-page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-lg bg-notaria-blue-2 px-3 py-2 text-white"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage <= 1}
                className={`rounded-lg px-3 py-2 font-semibold ${currentPage <= 1 ? 'bg-notaria-blue-2/50 text-gray-400' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
              >
                Anterior
              </button>
              <span className="px-2">Página {currentPage} de {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className={`rounded-lg px-3 py-2 font-semibold ${currentPage >= totalPages ? 'bg-notaria-blue-2/50 text-gray-400' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
              >
                Siguiente
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-notaria-blue-2 text-left">
                  <th className="p-3">Sel.</th>
                  <th className="p-3">Mes</th>
                  <th className="p-3">Día</th>
                  <th className="p-3">Vence</th>
                  <th className="p-3">Matrizador</th>
                  <th className="p-3">Empresa</th>
                  <th className="p-3">Responsable</th>
                  <th className="p-3">Trámite</th>
                  <th className="p-3">Monto</th>
                  <th className="p-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {loadingData ? (
                  <tr><td colSpan="10" className="p-4 text-center text-gray-300">Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="10" className="p-4 text-center text-gray-400">No hay trámites con los filtros actuales.</td></tr>
                ) : paginatedRows.map((row) => {
                  const responsibleLabel = `${row.responsible?.nombre || ''}${row.responsible?.departamento ? ` - ${row.responsible.departamento}` : ''}`;
                  return (
                    <tr key={row.id} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
                      <td className="p-3">
                        {canSelectRow(row) && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(String(row.id))}
                            onChange={() => toggleSelected(row.id)}
                            className="h-5 w-5 rounded text-notaria-yellow"
                          />
                        )}
                      </td>
                      <td className="p-3">{row.periodo_year}-{String(row.periodo_month).padStart(2, '0')}</td>
                      <td className="p-3">{row.fecha_tramite}</td>
                      <td className="p-3">{row.fecha_vencimiento}</td>
                      <td className="p-3">{row.usuario_matrizador}</td>
                      <td className="p-3">{row.company?.nombre || '-'}</td>
                      <td className="p-3">{responsibleLabel || '-'}</td>
                      <td className="p-3">
                        <div className="max-h-20 overflow-y-auto">
                          <p className="font-semibold">{row.tramite?.no_temporal || '-'}</p>
                          <p className="text-xs text-gray-300">{row.tramite?.acto || '-'}</p>
                        </div>
                      </td>
                      <td className="p-3 font-semibold">{toMoney(row.monto)}</td>
                      <td className="p-3">{row.estado}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <CatalogModal open={companyModalOpen} title="Añadir empresa" onClose={() => setCompanyModalOpen(false)}>
        <form onSubmit={submitCompany} className="space-y-3">
          <input required value={companyForm.nombre} onChange={(e) => setCompanyForm((prev) => ({ ...prev, nombre: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Nombre empresa" />
          <input value={companyForm.ruc} onChange={(e) => setCompanyForm((prev) => ({ ...prev, ruc: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="RUC" />
          <input value={companyForm.email} onChange={(e) => setCompanyForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Email" />
          <input value={companyForm.telefono} onChange={(e) => setCompanyForm((prev) => ({ ...prev, telefono: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Teléfono" />
          <button className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">Guardar empresa</button>
        </form>
      </CatalogModal>

      <CatalogModal open={responsibleModalOpen} title="Añadir responsable" onClose={() => setResponsibleModalOpen(false)}>
        <form onSubmit={submitResponsible} className="space-y-3">
          <select required value={responsibleForm.company} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, company: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white">
            <option value="">Empresa</option>
            {companies.map((company) => <option key={company.id} value={company.id}>{company.nombre}</option>)}
          </select>
          <input required value={responsibleForm.nombre} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, nombre: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Nombre responsable" />
          <input value={responsibleForm.departamento} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, departamento: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Departamento" />
          <input value={responsibleForm.email} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Email" />
          <input value={responsibleForm.telefono} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, telefono: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Teléfono" />
          <button className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">Guardar responsable</button>
        </form>
      </CatalogModal>

      <CatalogModal open={Boolean(reviewModal)} title={reviewModal === 'aprobar' ? 'Aprobar créditos' : 'Rechazar créditos'} onClose={() => setReviewModal(null)}>
        <div className="space-y-3">
          <p>{selectedRows.length} trámites seleccionados por {toMoney(selectedTotal)}.</p>
          <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Notas" />
          <button onClick={reviewSelected} className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">
            Confirmar
          </button>
        </div>
      </CatalogModal>

      <CatalogModal open={collectModalOpen} title="Justificar cobro mensual" onClose={() => setCollectModalOpen(false)}>
        <form onSubmit={collectSelected} className="space-y-3">
          <div className="rounded-lg bg-notaria-blue-2 p-3 text-sm">
            <p>Mes calculado: <span className="font-bold">{selectedPeriodLabel || 'Varios meses'}</span></p>
            <p>Total comprobante: <span className="font-bold">{toMoney(selectedTotal)}</span></p>
          </div>
          <select value={collectForm.tipo_pago} onChange={(e) => setCollectForm((prev) => ({ ...prev, tipo_pago: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white">
            <option value="transferencia">Transferencia</option>
            <option value="deposito">Depósito</option>
            <option value="tarjeta_credito">Tarjeta de Crédito</option>
            <option value="nota_credito">Nota de Crédito</option>
          </select>
          <input required value={collectForm.numero_transaccion} onChange={(e) => setCollectForm((prev) => ({ ...prev, numero_transaccion: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Número de transacción" />
          <input required type="date" value={collectForm.fecha_pago} onChange={(e) => setCollectForm((prev) => ({ ...prev, fecha_pago: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" />
          <input required type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setCollectForm((prev) => ({ ...prev, archivo: e.target.files?.[0] || null }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" />
          <textarea value={collectForm.notes} onChange={(e) => setCollectForm((prev) => ({ ...prev, notes: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Notas" />
          <button className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">Registrar comprobante mensual</button>
        </form>
      </CatalogModal>
    </motion.div>
  );
};

const SummaryList = ({ title, rows }) => (
  <div className="rounded-lg bg-notaria-blue-1/80 p-4">
    <p className="mb-2 text-sm font-semibold text-gray-200">{title}</p>
    <div className="max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
      {rows.length === 0 ? (
        <p className="text-gray-400">Sin datos</p>
      ) : rows.slice(0, 6).map((row) => (
        <div key={row.key} className="flex justify-between gap-3">
          <span className="truncate">{row.label}</span>
          <span className="shrink-0 font-semibold">{toMoney(row.total)}</span>
        </div>
      ))}
    </div>
  </div>
);

const CatalogModal = ({ open, title, onClose, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="w-full max-w-lg rounded-xl bg-notaria-blue-1 p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold">{title}</h2>
            <button onClick={onClose} className="text-gray-300 hover:text-notaria-yellow">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          {children}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default CreditReceivables;
